import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { taskNavRef } from '../utils/taskNav';
import { saveGuestQuiz } from '../utils/guestPractice';

const GROUP_OPTIONS = [
  { value: 'all', label: 'Все' },
  { value: 'oge', label: 'ОГЭ' },
  { value: 'basics', label: 'Основы' },
  { value: 'ege', label: 'ЕГЭ' },
  { value: 'regex', label: 'Регулярки' },
  { value: 'excel', label: 'Excel' },
];

const GROUP_LABELS = {
  oge: 'ОГЭ',
  basics: 'Основы',
  ege: 'ЕГЭ',
  regex: 'Регулярки',
  excel: 'Excel',
};

const GROUP_ORDER = ['oge', 'basics', 'ege', 'regex', 'excel'];

function pluralQuestions(n) {
  if (n === 1) return 'вопрос';
  if (n >= 2 && n <= 4) return 'вопроса';
  return 'вопросов';
}

function pluralTests(n) {
  if (n === 1) return 'тест';
  if (n >= 2 && n <= 4) return 'теста';
  return 'тестов';
}

function egeSectionLabel(examKey) {
  if (!examKey) return 'ЕГЭ';
  return `ЕГЭ-${examKey.replace('-', '–')}`;
}

function QuizCard({ quiz, startingId, onStart }) {
  return (
    <li className="practice-quizzes-item">
      <div className="practice-quizzes-item-main">
        <h2 className="practice-quizzes-item-title">{quiz.title}</h2>
        <p className="practice-quizzes-item-meta text-secondary">
          {quiz.task_count} {pluralQuestions(quiz.task_count)}
          {quiz.topic_tags?.length
            ? ` · ${quiz.topic_tags.map((t) => t.name).join(', ')}`
            : ''}
        </p>
      </div>
      <div className="practice-quizzes-item-actions">
        {quiz.catalog_query ? (
          <Link to={`/catalog?${quiz.catalog_query}`} className="btn btn-outline btn-sm">
            В каталоге
          </Link>
        ) : null}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={startingId === quiz.id}
          onClick={() => void onStart(quiz)}
        >
          {startingId === quiz.id ? 'Открываю…' : 'Решать'}
        </button>
      </div>
    </li>
  );
}

function FlatQuizList({ quizzes, startingId, onStart }) {
  if (!quizzes.length) {
    return <p className="text-secondary">В этом разделе пока нет тестов.</p>;
  }
  return (
    <ul className="practice-quizzes-list">
      {quizzes.map((quiz) => (
        <QuizCard key={quiz.id} quiz={quiz} startingId={startingId} onStart={onStart} />
      ))}
    </ul>
  );
}

function EgeAccordion({ quizzes, startingId, onStart }) {
  const buckets = useMemo(() => {
    const map = new Map();
    for (const quiz of quizzes) {
      const key = quiz.exam_key || 'other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(quiz);
    }
    return Array.from(map.entries());
  }, [quizzes]);

  const [expanded, setExpanded] = useState(() => new Set());

  if (!quizzes.length) {
    return <p className="text-secondary">В этом разделе пока нет тестов.</p>;
  }

  function toggle(key) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <ul className="practice-quizzes-ege-tree">
      {buckets.map(([examKey, items]) => {
        const open = expanded.has(examKey);
        const totalQuestions = items.reduce((sum, q) => sum + (q.task_count || 0), 0);
        return (
          <li key={examKey} className="practice-quizzes-ege-node">
            <button
              type="button"
              className="practice-quizzes-ege-toggle"
              aria-expanded={open}
              onClick={() => toggle(examKey)}
            >
              <span className="practice-quizzes-ege-toggle-label">
                {egeSectionLabel(examKey)}
              </span>
              <span className="practice-quizzes-ege-toggle-meta text-secondary">
                {items.length} {pluralTests(items.length)}
                {totalQuestions ? ` · ${totalQuestions} ${pluralQuestions(totalQuestions)}` : ''}
              </span>
              <span className="practice-quizzes-ege-chevron" aria-hidden>
                {open ? '▾' : '▸'}
              </span>
            </button>
            {open ? (
              <ul className="practice-quizzes-list practice-quizzes-ege-children">
                {items.map((quiz) => (
                  <QuizCard
                    key={quiz.id}
                    quiz={quiz}
                    startingId={startingId}
                    onStart={onStart}
                  />
                ))}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Каталог пробных тестов: список заготовок + старт своего списка для решения.
 */
export default function PracticeQuizzesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const groupParam = searchParams.get('group') || 'all';
  const activeGroup = GROUP_OPTIONS.some((o) => o.value === groupParam) ? groupParam : 'all';

  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startingId, setStartingId] = useState(null);
  const [shuffleQuestions, setShuffleQuestions] = useState(true);
  const [collapsedSections, setCollapsedSections] = useState(() => new Set(GROUP_ORDER));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/tasks/practice-quizzes/');
        if (!cancelled) setQuizzes(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setError('Не удалось загрузить список тестов.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const byGroup = useMemo(() => {
    const result = { oge: [], basics: [], ege: [], regex: [], excel: [] };
    for (const quiz of quizzes) {
      const g = result[quiz.group] ? quiz.group : 'basics';
      result[g].push(quiz);
    }
    return result;
  }, [quizzes]);

  function setGroup(value) {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') next.delete('group');
    else next.set('group', value);
    setSearchParams(next, { replace: true });
  }

  function toggleSection(group) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }

  async function startQuiz(quiz) {
    setStartingId(quiz.id);
    setError('');
    try {
      if (user) {
        const res = await api.post(`/tasks/practice-quizzes/${quiz.id}/start/`, {
          shuffle: shuffleQuestions,
        });
        const listId = res.data?.list_id;
        const first = res.data?.first_task;
        const ref = taskNavRef(first);
        if (listId && ref) {
          navigate(`/work/${ref}?list=${listId}`);
          return;
        }
        setError('Тест создан, но не удалось открыть первую задачу.');
        return;
      }
      const res = await api.post(`/tasks/practice-quizzes/${quiz.id}/guest-start/`, {
        shuffle: shuffleQuestions,
      });
      const first = res.data?.first_task;
      const ref = taskNavRef(first);
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      if (!ref || !items.length) {
        setError('Не удалось открыть первую задачу.');
        return;
      }
      saveGuestQuiz({
        title: res.data.title || quiz.title,
        student_id: null,
        items,
      });
      navigate(`/work/${ref}?guest=1`);
    } catch {
      setError('Не удалось начать тест.');
    } finally {
      setStartingId(null);
    }
  }

  function renderGroupBody(group) {
    const items = byGroup[group] || [];
    if (group === 'ege') {
      return (
        <EgeAccordion quizzes={items} startingId={startingId} onStart={startQuiz} />
      );
    }
    return <FlatQuizList quizzes={items} startingId={startingId} onStart={startQuiz} />;
  }

  return (
    <div className="practice-quizzes-page">
      <div className="catalog-page-header">
        <h1 className="catalog-page-title">Тесты</h1>
        <p className="catalog-page-lead">
          Короткие проверки по темам.
          {user
            ? ' Кнопка «Решать» создаёт ваш список задач.'
            : ' Результаты не сохраняются — войдите, чтобы вести историю.'}
          {' '}
          В каталоге те же вопросы отмечены тегом «Тест» — можно фильтровать вместе с темой
          (например, «Циклы» + «Тест»).
        </p>
        <div className="checkbox-row practice-quizzes-shuffle">
          <label title="Случайный порядок вопросов">
            <input
              type="checkbox"
              checked={shuffleQuestions}
              onChange={(e) => setShuffleQuestions(e.target.checked)}
            />
            Перемешать
          </label>
        </div>
      </div>

      <div className="konspekty-scope-bar practice-quizzes-group-bar" role="group" aria-label="Разделы тестов">
        {GROUP_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            className={activeGroup === o.value ? 'konspekty-scope-btn active' : 'konspekty-scope-btn'}
            onClick={() => setGroup(o.value)}
          >
            {o.label}
            {o.value !== 'all' && byGroup[o.value]?.length
              ? ` (${byGroup[o.value].length})`
              : o.value === 'all' && quizzes.length
                ? ` (${quizzes.length})`
                : ''}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <p className="text-secondary">Загрузка…</p>
      ) : quizzes.length === 0 ? (
        <p className="text-secondary">Пока нет опубликованных тестов.</p>
      ) : activeGroup === 'all' ? (
        <div className="practice-quizzes-sections">
          {GROUP_ORDER.filter((g) => (byGroup[g] || []).length > 0).map((group) => {
            const open = !collapsedSections.has(group);
            const count = byGroup[group].length;
            return (
              <section key={group} className="practice-quizzes-section">
                <button
                  type="button"
                  className="practice-quizzes-section-toggle"
                  aria-expanded={open}
                  onClick={() => toggleSection(group)}
                >
                  <span>{GROUP_LABELS[group]}</span>
                  <span className="text-secondary">
                    {count} {pluralTests(count)}
                  </span>
                  <span className="practice-quizzes-ege-chevron" aria-hidden>
                    {open ? '▾' : '▸'}
                  </span>
                </button>
                {open ? renderGroupBody(group) : null}
              </section>
            );
          })}
        </div>
      ) : (
        renderGroupBody(activeGroup)
      )}
    </div>
  );
}
